// CreateDoctor.tsx
import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  requiredValidator,
  emailWOValueValidator,
  phoneValidator,
} from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  GENDER_OPTIONS,
  LANGUAGES_LIST,
  STATUSARRAY,
} from "../../_contstants/common";
import {
  createDoctor,
  getDoctorByID,
  getDoctorImagesByDoctorID,
  updateDoctor,
  uploadDoctorMultipleImages,
} from "./services/doctor.service";
import { clearDoctorDetails } from "./doctorSlice";
import { getFile } from "../../_helper/helper";
import { getAllActiveCities } from "../city/services/city.services";
import { getAllActiveStates } from "../state/services/state.services";
import { getAllActiveCountries } from "../country/services/country.services";
import DoctorImageForm from "./DoctorImageForm";
import DoctorRatingsForm from "./DoctorRatingsForm";
import DoctorExpForm from "./DoctorExpForm";
import DoctorDegreeForm from "./DoctorDegreeForm";
import DoctorFAQForm from "./DoctorFAQForm";
import DoctorTimingForm from "./DoctorTimingForm";
import DoctorShiftForm from "./DoctorShiftForm";
import { getAllActiveSpecialties } from "../specialties/services/specialties.service";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import FormRichTextField from "../../components/formFields/FormRichTextField";
interface StateChangeWatcherProps {
  formRenderProps: FormRenderProps;
}

const StateChangeWatcher: React.FC<StateChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const cityID = formRenderProps.valueGetter("CityID");
  const cities = useAppSelector((state) => state.city?.CityList || []);
  const stateList = useAppSelector((state) => state.state?.StateList || []);

  useEffect(() => {
    if (!cityID) {
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
      return;
    }

    const city = cities.find((c: any) => c.ID === cityID);

    if (city) {
      formRenderProps.onChange("StateID", {
        value: city.StateID,
      });

      const state = stateList.find((s: any) => s.ID === city.StateID);

      if (state) {
        formRenderProps.onChange("CountryID", {
          value: state.CountryID,
        });
      } else {
        formRenderProps.onChange("CountryID", { value: "" });
      }
    } else {
      formRenderProps.onChange("StateID", { value: "" });
      formRenderProps.onChange("CountryID", { value: "" });
    }
  }, [cityID, cities, stateList]);

  return null;
};

const CreateDoctor: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const DoctorID = location.state?.DoctorID;

  const DoctorDetail = useAppSelector((state) => state.doctor.DoctorDetail);
  const loading = useAppSelector((state) => state.doctor.formLoading);
  const cities = useAppSelector((state) => state.city?.CityList);
  const states = useAppSelector((state) => state.state?.StateList);
  const countries = useAppSelector((state) => state.country?.CountryList);
  const Specialty = useAppSelector((state) => state.specialty?.SpecialtyList);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [DoctorDetail]);

  useEffect(() => {
    dispatch(getAllActiveCities());
    dispatch(getAllActiveStates());
    dispatch(getAllActiveCountries());
    dispatch(getAllActiveSpecialties());
    if (DoctorID) {
      dispatch(getDoctorByID(DoctorID));
      dispatch(getDoctorImagesByDoctorID(DoctorID));
    }
    return () => {
      dispatch(clearDoctorDetails());
    };
  }, [DoctorID]);

  const handleSubmit = async (values: any) => {
    const ImageData = new FormData();
    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? getFile(values?.MainImage[0])
        : "";
    const RegistrationCertificateImage =
      values?.RegistrationCertificateImage &&
      typeof values?.RegistrationCertificateImage !== "string"
        ? getFile(values?.RegistrationCertificateImage[0])
        : "";
    const DegreeCertificateImage =
      values?.DegreeCertificateImage &&
      typeof values?.DegreeCertificateImage !== "string"
        ? getFile(values?.DegreeCertificateImage[0])
        : "";

    const Speciality = Array.isArray(values.Speciality)
      ? values.Speciality.join(",")
      : "";
    const LanguagesSpoken = Array.isArray(values.LanguagesSpoken)
      ? values.LanguagesSpoken.join(",")
      : "";

    const payload = {
      ID: DoctorID || 0,
      DoctorName: values.DoctorName ?? "",
      Speciality: Speciality ?? "",
      Experience: values.Experience ? Number(values.Experience) : 0,
      IsMedical: values.IsMedical ?? false,
      MedicalRegDetails: values.MedicalRegDetails ?? "",
      AboutDoctor: values.AboutDoctor ?? "",
      Address: values.Address ?? "",
      Area: values.Area ?? "",
      CityID: values.CityID ?? 0,
      StateID: values.StateID ?? 0,
      CountryID: values.CountryID ?? 0,
      Fees: values.Fees ? Number(values.Fees) : 0,
      Gender: values.Gender ?? "",
      MobileNo: values.MobileNo ?? "",
      EmailID: values.EmailID ?? "",
      HospitalAffiliation: values.HospitalAffiliation ?? "",
      LanguagesSpoken: LanguagesSpoken ?? "",
      IsActive: values.IsActive === 1,
    };

    try {
      if (DoctorID) {
        const response = await dispatch(updateDoctor(payload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.MainImage && typeof values?.MainImage !== "string") ||
            (values?.RegistrationCertificateImage &&
              typeof values?.RegistrationCertificateImage !== "string") ||
            (values?.DegreeCertificateImage &&
              typeof values?.DegreeCertificateImage !== "string") ||
            values.attachment?.length > 0
          ) {
            ImageData.append("DoctorID", DoctorID);
            ImageData.append("MainImage", MainImage);
            ImageData.append(
              "RegistrationCertificateImage",
              RegistrationCertificateImage,
            );
            ImageData.append("DegreeCertificateImage", DegreeCertificateImage);
            if (values.attachment?.length > 0) {
              const Images = values.attachment?.map((fileItem: any) =>
                fileItem.getRawFile(),
              );
              Images?.map((image: any) =>
                ImageData.append("OtherImage", image),
              );
            }
            await dispatch(uploadDoctorMultipleImages(ImageData));
          }
          navigate("/doctor");
          // dispatch(getDoctorByID(DoctorID));
        }
      } else {
        const response = await dispatch(createDoctor(payload));
        const ID = response.payload?.Data?.ID;
        if (response?.meta?.requestStatus === "fulfilled" && ID) {
          if (
            (values?.MainImage && typeof values?.MainImage !== "string") ||
            (values?.RegistrationCertificateImage &&
              typeof values?.RegistrationCertificateImage !== "string") ||
            (values?.DegreeCertificateImage &&
              typeof values?.DegreeCertificateImage !== "string") ||
            values.attachment?.length > 0
          ) {
            ImageData.append("DoctorID", ID);
            ImageData.append("MainImage", MainImage);
            ImageData.append(
              "RegistrationCertificateImage",
              RegistrationCertificateImage,
            );
            ImageData.append("DegreeCertificateImage", DegreeCertificateImage);
            if (values.attachment?.length > 0) {
              const Images = values.attachment?.map((fileItem: any) =>
                fileItem.getRawFile(),
              );
              Images?.map((image: any) =>
                ImageData.append("OtherImage", image),
              );
            }
            await dispatch(uploadDoctorMultipleImages(ImageData));
          }
          navigate("/doctor/edit", { state: { DoctorID: ID } });
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
    }
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={DoctorDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <StateChangeWatcher formRenderProps={formRenderProps} />
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 65 }}
                gap={{ rows: 0, cols: 20 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                {/* Header */}
                <GridLayoutItem colSpan={3}>
                  <Typography.h4 style={{ margin: 0 }}>
                    {DoctorID ? "Update Doctor" : "Add New Doctor"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "flex-start",
                    gap: "10px",
                  }}
                >
                  <ButtonWithLoading
                    label={DoctorID ? "Update Doctor" : "Save Doctor"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/doctor")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>

                {/* Section: Basic Info */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 20,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Basic Information
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="DoctorName"
                    label="Doctor Name"
                    placeholder="Full name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2} rowSpan={2}>
                  <Field
                    justifyContent="start"
                    name="MainImage"
                    label="Doctor Image"
                    alternateName="Doctor Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="Experience"
                    label="Experience (Years)"
                    placeholder="e.g., 10"
                    component={FormTextField}
                    type="number"
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="Fees"
                    label="Consultation Fees (₹)"
                    placeholder="e.g., 500"
                    component={FormTextField}
                    type="number"
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Speciality"
                    label="Speciality"
                    placeholder="e.g., Cardiology"
                    isAddNew={true}
                    drawerName="specialityForm"
                    component={FormMultiSelectionFiled}
                    options={Specialty?.map((item) => ({
                      value: item.ID,
                      label: item.Title,
                    }))}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="Gender"
                    label="Gender"
                    placeholder="Select gender"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={GENDER_OPTIONS}
                  />
                </GridLayoutItem>

                {/* <GridLayoutItem colSpan={1}>
                  <Field
                    name="HospitalAffiliation"
                    label="Hospital Affiliation"
                    placeholder="Affiliated hospital name"
                    component={FormTextField}
                  />
                </GridLayoutItem> */}

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="LanguagesSpoken"
                    label="Languages Spoken"
                    placeholder="e.g., Hindi, English"
                    component={FormMultiSelectionFiled}
                    options={LANGUAGES_LIST}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={4}>
                  <Field
                    name="AboutDoctor"
                    label="About Doctor"
                    placeholder="Detailed description about the doctor"
                    component={FormRichTextField}
                  />
                </GridLayoutItem>
                {/* Section: Medical Registration */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Medical Registration Verification
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={1}
                  style={{
                    width: "100%",
                    margin: "18px 0px 0px",
                    padding: "0px 10px 8px",
                    fontWeight: 700,
                    background: "whitesmoke",
                    borderRadius: 5,
                    height: 48,
                  }}
                >
                  <Field
                    name="IsMedical"
                    label="Is Medically Registered Verified"
                    component={FormCheckbox}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={3}>
                  <Field
                    name="MedicalRegDetails"
                    label="Medical Registration Details"
                    placeholder="Registration number / council details"
                    component={FormTextField}
                    // validator={requiredValidator}
                    // astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem className="d-flex gap-2" colSpan={4}>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    avatarStyle={{ margin: 0 }}
                    justifyContent="start"
                    name="RegistrationCertificateImage"
                    label="Registration Certificate Image"
                    alternateName="Registration Certificate Image"
                    component={FormImageUpload}
                    AvatarHeightWidth={70}
                  />
                  <div className="px-2"></div>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    avatarStyle={{ margin: 0 }}
                    justifyContent="start"
                    name="DegreeCertificateImage"
                    label="Degree Certificate Image"
                    alternateName="Degree Certificate Image"
                    component={FormImageUpload}
                    AvatarHeightWidth={70}
                  />
                </GridLayoutItem>

                {/* Section: Contact */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Contact Details
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="MobileNo"
                    label="Mobile No"
                    placeholder="Mobile number"
                    component={FormTextField}
                    validator={phoneValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="EmailID"
                    label="Email ID"
                    placeholder="Email address"
                    component={FormTextField}
                    validator={emailWOValueValidator}
                  />
                </GridLayoutItem>

                {/* Section: Location */}
                <GridLayoutItem colSpan={4}>
                  <div
                    style={{
                      backgroundColor: "#f8f9fa",
                      padding: "12px 16px",
                      borderRadius: "8px",
                      marginTop: 30,
                    }}
                  >
                    <Typography.h5 style={{ margin: 0, color: "#495057" }}>
                      Location
                    </Typography.h5>
                  </div>
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Address"
                    label="Address"
                    placeholder="Full address"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2}>
                  <Field
                    name="Area"
                    label="Area / Locality"
                    placeholder="Area or locality"
                    component={FormTextField}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="CityID"
                    label="City"
                    placeholder="Select city"
                    isAddNew={true}
                    drawerName="cityForm"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={cities.map((city: any) => ({
                      value: city.ID,
                      label: city.CityName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="StateID"
                    label="State"
                    placeholder="Auto-filled from city"
                    validator={requiredValidator}
                    astrike={true}
                    component={FormSelectionField}
                    disabled={true}
                    options={states.map((state: any) => ({
                      value: state.ID,
                      label: state.StateName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={1}>
                  <Field
                    name="CountryID"
                    label="Country"
                    placeholder="Select country"
                    validator={requiredValidator}
                    astrike={true}
                    component={FormSelectionField}
                    disabled={true}
                    options={countries.map((country: any) => ({
                      value: country.ID,
                      label: country.CountryName,
                    }))}
                  />
                </GridLayoutItem>

                {/* Section: Status */}
                <GridLayoutItem colSpan={1}>
                  <Field
                    name="IsActive"
                    label="Status"
                    placeholder="Select status"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY.map((status: any) => ({
                      value: status.value,
                      label: status.label,
                    }))}
                  />
                </GridLayoutItem>

                {/* Footer Buttons */}
                <GridLayoutItem
                  colSpan={4}
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "10px",
                    marginBlock: 20,
                  }}
                >
                  <ButtonWithLoading
                    label={DoctorID ? "Update Doctor" : "Save Doctor"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                    loading={loading}
                  />
                  <Button
                    type="button"
                    fillMode={"outline"}
                    themeColor={"primary"}
                    onClick={() => navigate("/doctor")}
                  >
                    Cancel
                  </Button>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
            <DoctorImageForm formRenderProps={formRenderProps} />
          </FormElement>
        )}
      />
      {DoctorID && <DoctorDegreeForm />}
      {DoctorID && <DoctorExpForm />}
      {DoctorID && <DoctorFAQForm />}
      {DoctorID && <DoctorTimingForm />}
      {DoctorID && <DoctorShiftForm />}
      {DoctorID && <DoctorRatingsForm />}
    </>
  );
};

export default CreateDoctor;
